import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Shield,
  Sparkles,
  CheckCircle2,
  PhoneCall,
  Mail,
  MapPin,
  Globe,
  Smartphone,
  Users,
  Award,
  ChevronRight,
  Menu,
  X,
  ArrowRight,
  Fingerprint,
  QrCode,
  FileSpreadsheet,
  CalendarCheck,
  Building2,
  HardHat,
  HeartHandshake,
  Send,
  MessageCircle,
  ShieldCheck,
  Activity,
  Layers,
  ChevronDown
} from 'lucide-react';
import logoLuj from './LOGOLUJ.png';

// ============================================================
// CUSTOM HOOK: useReveal — Intersection Observer scroll reveal
// Usage: const ref = useReveal();
// Apply: <div ref={ref} className="reveal-on-scroll reveal-up delay-200">
// ============================================================
function useReveal(options = {}) {
  const { threshold = 0.12, rootMargin = '0px 0px -60px 0px' } = options;
  const observer = useRef(null);

  const ref = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();
      if (!node) return;

      observer.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              // Once revealed, stop observing (one-shot)
              observer.current.unobserve(entry.target);
            }
          });
        },
        { threshold, rootMargin }
      );

      observer.current.observe(node);
    },
    [threshold, rootMargin]
  );

  return ref;
}

// Observe a container and reveal all .reveal-on-scroll children
function useRevealChildren(options = {}) {
  const { threshold = 0.1, rootMargin = '0px 0px -40px 0px' } = options;
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const targets = container.querySelectorAll('.reveal-on-scroll');
    if (!targets.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    targets.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return containerRef;
}

export default function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeAppTab, setActiveAppTab] = useState('security'); // 'security' | 'clean'
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    service: 'security',
    personnelCount: '1-5',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  // ── Section reveal refs ──
  const revealProfil    = useRevealChildren();
  const revealLayanan   = useRevealChildren();
  const revealKeunggulan = useRevealChildren();
  const revealTeknologi = useRevealChildren();
  const revealKlien     = useRevealChildren();
  const revealFaq       = useRevealChildren();
  const revealKontak    = useRevealChildren();

  // Individual element reveals
  const revealProfilTitle   = useReveal();
  const revealLayananTitle  = useReveal();
  const revealKeunggTitle   = useReveal();
  const revealTeknologiTitle = useReveal();
  const revealKlienTitle    = useReveal();
  const revealFaqTitle      = useReveal();
  const revealKontakTitle   = useReveal();

  // Handle navbar background on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const text = `Halo PT Lima Utama Jaya, saya ingin konsultasi layanan outsourcing:
Nama: ${formData.name}
Perusahaan/Instansi: ${formData.company || '-'}
No. WhatsApp: ${formData.phone}
Layanan yang dibutuhkan: ${formData.service === 'security' ? 'Jasa Keamanan (Security)' : formData.service === 'cleaning' ? 'Jasa Kebersihan (Cleaning Service)' : 'Keamanan & Kebersihan (Integrated)'}
Estimasi Personil: ${formData.personnelCount} orang
Catatan: ${formData.message || '-'}`;

    const waUrl = `https://wa.me/6285156906144?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank');
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  const quickWhatsapp = (customText) => {
    const defaultText = customText || "Halo PT Lima Utama Jaya, saya ingin konsultasi mengenai layanan outsourcing keamanan dan kebersihan.";
    window.open(`https://wa.me/6285156906144?text=${encodeURIComponent(defaultText)}`, '_blank');
  };

  const clients = [
    { name: "BSB City", type: "Integrated Township", tag: "Kawasan & Residensial" },
    { name: "RSUD Majalengka", type: "Healthcare Facility", tag: "Fasilitas Kesehatan" },
    { name: "Miracle Clinic", type: "Aesthetic Clinic", tag: "Klinik & Komersial" },
    { name: "PT Indo Asia Tirta Manunggal", type: "Manufacturing & Industry", tag: "Pabrik / Manufaktur" },
    { name: "Tata Group", type: "Enterprise & Corporate", tag: "Perkantoran & Bisnis" },
  ];

  const faqs = [
    {
      q: "Bagaimana proses seleksi tenaga kerja di PT Lima Utama Jaya?",
      a: "Setiap personil keamanan dan kebersihan melewati proses seleksi ketat meliputi verifikasi identitas, SKCK kepolisian, tes kesehatan & fisik, psikotes dasar, serta wajib memiliki sertifikasi resmi (Gada Pratama untuk Security dan sertifikasi BNSP/Internal untuk Cleaning Service)."
    },
    {
      q: "Bagaimana jika ada personil yang berhalangan hadir atau sakit?",
      a: "PT Lima Utama Jaya menyediakan backup personil / relief staff yang selalu siaga. Pergantian personil dilakukan secara cepat tanpa mengganggu jadwal operasional di lokasi klien dan tanpa biaya tambahan."
    },
    {
      q: "Bagaimana sistem pengawasan operasional dan kehadiran tenaga kerja?",
      a: "Kami menggunakan teknologi aplikasi Clean App & Security App yang terintegrasi. Absensi menggunakan geofencing & selfie barcode, patroli terekam secara digital, serta supervisor kami rutin melakukan inspeksi mendadak (Sidak) dan audit berkala."
    },
    {
      q: "Apakah seluruh urusan legalitas dan ketenagakerjaan sudah ditanggung LUJ?",
      a: "Ya, 100%. Mulai dari gaji sesuai regulasi, BPJS Ketenagakerjaan & Kesehatan, THR, seragam, hingga kepatuhan hukum ketenagakerjaan sepenuhnya menjadi tanggung jawab PT Lima Utama Jaya."
    },
    {
      q: "Berapa lama estimasi penempatan personil setelah penandatanganan kontrak?",
      a: "Tergantung jumlah kebutuhan personil, estimasi deploy berkisar antara 3 hingga 7 hari kerja setelah peninjauan lokasi (site survey) dan penyelarasan SOP disetujui."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 antialiased font-sans">
      
      {/* 1. NAVBAR (Sticky & Elegan dengan Glassmorphism) */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100 py-3' 
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Brand Logo & Name */}
            <a href="#beranda" className="flex items-center gap-3 group">
              <div className="relative w-11 h-11 flex-shrink-0 transition-transform duration-300 group-hover:scale-105 animate-logo-entrance">
                <img 
                  src={logoLuj} 
                  alt="Logo PT Lima Utama Jaya" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-extrabold text-lg sm:text-xl tracking-tight text-slate-900 leading-tight">
                  PT LIMA UTAMA JAYA
                </span>
                <span className="text-[11px] font-semibold tracking-wider text-red-600 uppercase">
                  Security & Facility Service
                </span>
              </div>
            </a>

            {/* Desktop Navigation Menu */}
            <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
              <a href="#beranda" className="hover:text-red-600 transition-colors">Beranda</a>
              <a href="#profil" className="hover:text-red-600 transition-colors">Profil</a>
              <a href="#layanan" className="hover:text-red-600 transition-colors">Layanan</a>
              <a href="#keunggulan" className="hover:text-red-600 transition-colors">Keunggulan</a>
              <a href="#teknologi" className="hover:text-red-600 transition-colors">Aplikasi Digital</a>
              <a href="#klien" className="hover:text-red-600 transition-colors">Klien</a>
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => quickWhatsapp()}
                className="relative inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-full shadow-lg shadow-red-600/20 hover:bg-red-700 hover:shadow-red-600/35 transition-all duration-200 active:scale-95 group overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <PhoneCall className="w-4 h-4" />
                  Hubungi Kami
                </span>
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gradient-to-r from-red-700 to-rose-700 transition-transform duration-300 ease-out" />
              </button>
            </div>

            {/* Mobile Menu Toggle Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-xl text-slate-700 hover:text-red-600 hover:bg-slate-100 focus:outline-none transition-colors"
                aria-label="Toggle Navigation"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-xl px-5 pt-4 pb-6 mt-3 space-y-3 transition-all duration-300 animate-in fade-in slide-in-from-top-2">
            <a 
              href="#beranda" 
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-semibold text-slate-800 hover:text-red-600"
            >
              Beranda
            </a>
            <a 
              href="#profil" 
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-semibold text-slate-800 hover:text-red-600"
            >
              Profil & Visi Misi
            </a>
            <a 
              href="#layanan" 
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-semibold text-slate-800 hover:text-red-600"
            >
              Layanan Kami
            </a>
            <a 
              href="#keunggulan" 
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-semibold text-slate-800 hover:text-red-600"
            >
              Keunggulan (Bento)
            </a>
            <a 
              href="#teknologi" 
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-semibold text-slate-800 hover:text-red-600"
            >
              Teknologi Aplikasi
            </a>
            <a 
              href="#klien" 
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-semibold text-slate-800 hover:text-red-600"
            >
              Klien Terpercaya
            </a>
            <div className="pt-3">
              <button
                onClick={() => { setMobileMenuOpen(false); quickWhatsapp(); }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 font-semibold text-white bg-red-600 rounded-xl shadow-md active:scale-98"
              >
                <PhoneCall className="w-4 h-4" />
                Konsultasi WhatsApp
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* 2. HERO SECTION (Visual Impactful & Clean) */}
      <section id="beranda" className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        {/* Decorative Background Elements with floating animation */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-10 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-float-orb" />
          <div className="absolute top-40 right-10 w-80 h-80 bg-slate-400/10 rounded-full blur-3xl animate-float-orb-slow" />
          <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Column: Headlines & CTA */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              {/* Badge */}
              <div className="animate-headline delay-100 inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white border border-red-200/80 shadow-sm text-xs md:text-sm font-semibold text-red-700">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-ping" />
                <span>Solusi Outsourcing Terpercaya & Berizin Resmi</span>
              </div>

              {/* Main Headline */}
              <h1 className="animate-headline delay-200 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-950 leading-[1.15]">
                Penyedia Jasa Alih Daya <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-red-700 to-rose-600">Keamanan</span> dan Fasilitas <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-rose-600">Kebersihan</span>
              </h1>

              {/* Subheadline */}
              <p className="animate-headline delay-300 text-base sm:text-lg md:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Solusi terbaik untuk lingkungan bersih, rapi, dan aman. Didukung manajemen handal, tenaga kerja profesional, dan teknologi aplikasi terintegrasi.
              </p>

              {/* Action Buttons */}
              <div className="animate-headline delay-400 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                <a
                  href="#kontak"
                  className="btn-shimmer w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 text-base font-bold text-white bg-red-600 rounded-2xl shadow-xl shadow-red-600/25 hover:bg-red-700 hover:shadow-red-600/40 hover:-translate-y-0.5 transition-all duration-200"
                >
                  <span>Konsultasi Layanan</span>
                  <ArrowRight className="w-5 h-5" />
                </a>

                <a
                  href="#layanan"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-semibold text-slate-700 bg-white border border-slate-200 rounded-2xl hover:bg-slate-50 hover:text-red-600 hover:border-red-200 shadow-sm transition-all duration-200"
                >
                  <span>Eksplorasi Layanan</span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </a>
              </div>

              {/* Trust Stats Bar */}
              <div className="animate-headline delay-500 grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-slate-200/80">
                <div className="text-left">
                  <div className="animate-stat-reveal delay-500 text-2xl sm:text-3xl font-extrabold text-slate-900">500+</div>
                  <div className="text-xs text-slate-500 font-medium">Tenaga Siaga</div>
                </div>
                <div className="text-left">
                  <div className="animate-stat-reveal delay-600 text-2xl sm:text-3xl font-extrabold text-slate-900">100%</div>
                  <div className="text-xs text-slate-500 font-medium">Legalitas Resmi</div>
                </div>
                <div className="text-left">
                  <div className="animate-stat-reveal" style={{animationDelay:'700ms'}} >
                    <span className="text-2xl sm:text-3xl font-extrabold text-red-600">24/7</span>
                  </div>
                  <div className="text-xs text-slate-500 font-medium">Monitoring App</div>
                </div>
                <div className="text-left">
                  <div className="animate-stat-reveal" style={{animationDelay:'800ms'}}>
                    <span className="text-2xl sm:text-3xl font-extrabold text-slate-900">99.8%</span>
                  </div>
                  <div className="text-xs text-slate-500 font-medium">Kepuasan Klien</div>
                </div>
              </div>

            </div>

            {/* Right Column: High-tech Visual / Hero Mockup Card */}
            <div className="lg:col-span-5 relative animate-mockup-entrance">
              <div className="relative mx-auto max-w-md lg:max-w-none animate-phone-float">
                
                {/* Main Visual Glass Card */}
                <div className="relative rounded-3xl bg-gradient-to-b from-white to-slate-50/80 p-6 sm:p-8 border border-slate-200/90 shadow-2xl shadow-slate-200/60 overflow-hidden">
                  
                  {/* Card Header */}
                  <div className="flex items-center justify-between pb-6 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600">
                        <ShieldCheck className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Enterprise Security & Hygiene</div>
                        <div className="text-base font-bold text-slate-900">PT. Lima Utama Jaya</div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Live System
                    </span>
                  </div>

                  {/* Dual Service Live Monitor Showcase */}
                  <div className="space-y-4 my-6">
                    
                    {/* Security Live Feed Card */}
                    <div className="p-4 rounded-2xl bg-slate-900 text-white shadow-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-red-500" />
                          <span className="text-xs font-bold uppercase tracking-wider text-slate-200">Security App System</span>
                        </div>
                        <span className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-red-400 font-mono">Shift Siang Active</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-slate-800/80 p-2.5 rounded-xl border border-slate-700/60">
                          <div className="text-slate-400 text-[10px]">Patroli Checkpoint</div>
                          <div className="font-bold text-white text-sm">36 / 36 Titik</div>
                        </div>
                        <div className="bg-slate-800/80 p-2.5 rounded-xl border border-slate-700/60">
                          <div className="text-slate-400 text-[10px]">Presensi Digital</div>
                          <div className="font-bold text-emerald-400 text-sm">100% On-Time</div>
                        </div>
                      </div>
                    </div>

                    {/* Cleaning Live Feed Card */}
                    <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-red-600" />
                          <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Clean App Facility</span>
                        </div>
                        <span className="text-[11px] px-2 py-0.5 rounded bg-red-50 text-red-700 font-semibold">Audit Score 99.2%</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span className="flex items-center gap-1.5 font-medium">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Daily Sanitizing Done
                        </span>
                        <span className="text-slate-400 font-mono text-[11px]">Updated 5m ago</span>
                      </div>
                    </div>

                  </div>

                  {/* Card Footer Features */}
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span className="flex items-center gap-1">
                      <Fingerprint className="w-4 h-4 text-red-600" /> Face & GPS Verified
                    </span>
                    <span className="flex items-center gap-1">
                      <Award className="w-4 h-4 text-slate-700" /> Standar SOP ISO
                    </span>
                  </div>

                </div>

                {/* Floating Micro Badge */}
                <div className="absolute -bottom-6 -left-6 bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xl hidden sm:flex items-center gap-3 animate-float-badge">
                  <div className="w-9 h-9 rounded-xl bg-red-600 text-white flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900">Bebas Beban HRD</div>
                    <div className="text-[10px] text-slate-500">Payroll, BPJS & Backup Aman</div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3. PROFIL & VISI MISI (About Us - Clean Layout) */}
      <section id="profil" className="py-20 bg-white border-y border-slate-100 relative" ref={revealProfil}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-16" ref={revealProfilTitle}>
            <div className="reveal-on-scroll reveal-up inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-red-50 border border-red-100 text-xs font-bold text-red-600 uppercase tracking-wider">
              Tentang Kami
            </div>
            <h2 className="reveal-on-scroll reveal-up delay-150 text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight">
              Profil & Komitmen PT Lima Utama Jaya
            </h2>
            <p className="text-slate-600 text-base sm:text-lg">
              Mitra alih daya strategis dalam menghadirkan ekosistem kerja yang aman, bersih, dan higienis secara berkelanjutan.
            </p>
          </div>

          {/* About Company Grid */}
          <div className="grid lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left: Detailed Description Card */}
            <div className="reveal-on-scroll reveal-left lg:col-span-6 rounded-3xl bg-slate-50 p-8 sm:p-10 border border-slate-200/80 flex flex-col justify-between">
              <div className="space-y-5">
                <div className="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center font-black text-xl shadow-md shadow-red-600/20">
                  5
                </div>
                <h3 className="text-2xl font-bold text-slate-900 tracking-tight">
                  Dedikasi Penuh untuk Keunggulan Operasional Properti Anda
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  <strong className="text-slate-900">PT. Lima Utama Jaya</strong> adalah perusahaan penyedia jasa alih daya (outsourcing) terpercaya yang bergerak di bidang layanan jasa keamanan (Security Services) dan perawatan kebersihan fasilitas (Cleaning Services).
                </p>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  Kami melayani berbagai sektor mulai dari perumahan residensial, perkantoran modern, kawasan industri/pabrik, pusat perbelanjaan, institusi pendidikan, hingga rumah sakit dengan standar kualitas terbaik.
                </p>
              </div>

              {/* 4 Pillars of Excellence */}
              <div className="grid grid-cols-2 gap-4 pt-8 mt-6 border-t border-slate-200">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-slate-900 text-sm">Resmi & Berizin</div>
                    <div className="text-xs text-slate-500">Legalitas 100% lengkap</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-slate-900 text-sm">Tenaga Terlatih</div>
                    <div className="text-xs text-slate-500">Kualifikasi bersertifikat</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-slate-900 text-sm">Aplikasi Cerdas</div>
                    <div className="text-xs text-slate-500">Clean & Security App</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-slate-900 text-sm">Respon Cepat</div>
                    <div className="text-xs text-slate-500">Supervisi siaga 24 jam</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Visi & Misi Modern Card */}
            <div className="reveal-on-scroll reveal-right delay-200 lg:col-span-6 flex flex-col gap-6">
              
              {/* Visi Box */}
              <div className="p-8 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 text-white shadow-xl flex-1 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute right-0 top-0 w-48 h-48 bg-red-600/15 rounded-full blur-2xl" />
                <div className="relative z-10 space-y-3">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-bold uppercase tracking-wider">
                    Visi Perusahaan
                  </div>
                  <h4 className="text-xl sm:text-2xl font-bold text-white">
                    Mengangkat Martabat dan Kualitas Layanan Alih Daya
                  </h4>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                    Berkomitmen penuh menjadikan profesi kebersihan dan satpam sebagai layanan yang terhormat, proaktif, dan responsif melalui pembinaan terstruktur dan standardisasi modern.
                  </p>
                </div>
              </div>

              {/* Misi Box */}
              <div className="p-8 rounded-3xl bg-red-50/70 border border-red-100 flex-1 flex flex-col justify-center space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider self-start">
                  Misi Perusahaan
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      1
                    </div>
                    <p className="text-slate-700 text-sm">
                      Merekrut dan melatih tenaga kerja berintegritas tinggi dengan pembekalan keterampilan teknis dan hospitality.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      2
                    </div>
                    <p className="text-slate-700 text-sm">
                      Menerapkan sistem pengawasan berbasis teknologi aplikasi digital untuk transparansi dan kepuasan maksimal klien.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      3
                    </div>
                    <p className="text-slate-700 text-sm">
                      Memberikan perlindungan ketenagakerjaan yang adil dan menjamin kesejahteraan personil demi loyalitas kerja prima.
                    </p>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* 4. LAYANAN KAMI (Modern Card Layout) */}
      <section id="layanan" className="py-20 bg-slate-50 relative" ref={revealLayanan}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-16" ref={revealLayananTitle}>
            <div className="reveal-on-scroll reveal-up inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-red-50 border border-red-100 text-xs font-bold text-red-600 uppercase tracking-wider">
              Layanan Utama
            </div>
            <h2 className="reveal-on-scroll reveal-up delay-150 text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight">
              Solusi Alih Daya Terintegrasi & Andal
            </h2>
            <p className="reveal-on-scroll reveal-up delay-200 text-slate-600 text-base sm:text-lg">
              Dirancang untuk meningkatkan efisiensi operasional dan standar kenyamanan properti bisnis maupun pribadi Anda.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            
            {/* Service 1: Cleaning Service */}
            <div className="reveal-on-scroll reveal-left group rounded-3xl bg-white p-8 sm:p-10 border border-slate-200/90 shadow-lg shadow-slate-200/40 hover:shadow-2xl hover:shadow-red-500/10 hover:border-red-200 transition-all duration-300 flex flex-col justify-between">
              <div className="space-y-6">
                
                {/* Header Card */}
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 border border-red-100 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-colors duration-300">
                    <Sparkles className="w-7 h-7" />
                  </div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wider">
                    Facility Care
                  </span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-slate-900 group-hover:text-red-600 transition-colors">
                    Jasa Kebersihan & Perawatan Bangunan (Cleaning Service)
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    Layanan pemeliharaan kebersihan dan higienitas menyeluruh untuk gedung kantor, rumah sakit, pusat perbelanjaan, pabrik, dan perumahan.
                  </p>
                </div>

                {/* Service Features */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Daily Cleaning & Deep Cleaning Terjadwal</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Perawatan Lantai, Kaca Gedung & Sanitasi Khusus</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Peralatan Modern & Chemical Ramah Lingkungan</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Tenaga Terlatih dengan SOP Hospitality Tinggi</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Monitoring Mutu melalui <strong>Clean App</strong></span>
                  </div>
                </div>

              </div>

              {/* Action Button */}
              <div className="pt-8 mt-6 border-t border-slate-100">
                <button
                  onClick={() => quickWhatsapp("Halo PT Lima Utama Jaya, saya ingin informasi penawaran Jasa Kebersihan (Cleaning Service).")}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-sm text-red-600 bg-red-50 hover:bg-red-600 hover:text-white transition-all duration-200 group-hover:shadow-md"
                >
                  <span>Minta Penawaran Cleaning</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Service 2: Security Service */}
            <div className="reveal-on-scroll reveal-right delay-200 group rounded-3xl bg-white p-8 sm:p-10 border border-slate-200/90 shadow-lg shadow-slate-200/40 hover:shadow-2xl hover:shadow-red-500/10 hover:border-red-200 transition-all duration-300 flex flex-col justify-between">
              <div className="space-y-6">
                
                {/* Header Card */}
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-slate-900 text-white border border-slate-800 flex items-center justify-center group-hover:bg-red-600 transition-colors duration-300">
                    <Shield className="w-7 h-7" />
                  </div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-red-50 text-red-600 uppercase tracking-wider">
                    Asset Protection
                  </span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-slate-900 group-hover:text-red-600 transition-colors">
                    Jasa Keamanan & Pengamanan Aset (Security Services)
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    Pengamanan fisik komprehensif oleh satuan pengamanan terlatih dan berdedikasi untuk perlindungan aset, personil, dan ketertiban area 24/7.
                  </p>
                </div>

                {/* Service Features */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Satpam Bersertifikasi Resmi (Gada Pratama / Madya)</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Manajemen Patroli Presisi & Log Tamu/Barang</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Mitigasi Risiko, Tanggap Darurat & Penjagaan Pos</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Disiplin Tegas, Sikap Humanis & Siap Siaga</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span>Real-time Patrol Tracking via <strong>Security App</strong></span>
                  </div>
                </div>

              </div>

              {/* Action Button */}
              <div className="pt-8 mt-6 border-t border-slate-100">
                <button
                  onClick={() => quickWhatsapp("Halo PT Lima Utama Jaya, saya ingin informasi penawaran Jasa Pengamanan (Security Services).")}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-sm text-slate-800 bg-slate-100 hover:bg-slate-900 hover:text-white transition-all duration-200 group-hover:shadow-md"
                >
                  <span>Minta Penawaran Security</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 5. KEUNGGULAN & MANFAAT (Gaya Bento Grid Modern) */}
      <section id="keunggulan" className="py-20 bg-white relative" ref={revealKeunggulan}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-16" ref={revealKeunggTitle}>
            <div className="reveal-on-scroll reveal-up inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-red-50 border border-red-100 text-xs font-bold text-red-600 uppercase tracking-wider">
              Mengapa Memilih Kami
            </div>
            <h2 className="reveal-on-scroll reveal-up delay-150 text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight">
              Keunggulan Kompetitif PT Lima Utama Jaya
            </h2>
            <p className="reveal-on-scroll reveal-up delay-200 text-slate-600 text-base sm:text-lg">
              Solusi bebas repot yang memberikan rasa tenang, efisiensi anggaran, dan kualitas operasional terbaik.
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            
            {/* Bento 1: Large Span (Seleksi & SDM) */}
            <div className="reveal-on-scroll reveal-scale bento-shimmer md:col-span-2 lg:col-span-2 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-8 sm:p-10 text-white flex flex-col justify-between shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 rounded-full blur-3xl group-hover:bg-red-600/20 transition-all duration-500" />
              
              <div className="space-y-4 relative z-10">
                <div className="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-white tracking-tight">
                  Tenaga Kerja Profesional dengan Seleksi Ketat
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Kami memberlakukan standar rekrutmen berlapis mencakup verifikasi kepolisian (SKCK), tes fisik & kesehatan, uji psikotes, serta pembekalan SOP berstandar internasional sebelum penugasan ke lokasi Anda.
                </p>
              </div>

              <div className="pt-8 mt-6 border-t border-slate-800 flex flex-wrap gap-2 relative z-10">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-800/90 text-slate-200 border border-slate-700">
                  ✓ Gada Pratama Bersertifikat
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-800/90 text-slate-200 border border-slate-700">
                  ✓ Pelatihan Hospitality & K3
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-800/90 text-slate-200 border border-slate-700">
                  ✓ Evaluasi Kinerja Berkala
                </span>
              </div>
            </div>

            {/* Bento 2: Bebas Repot Administratif */}
            <div className="reveal-on-scroll reveal-up delay-200 bento-shimmer md:col-span-1 lg:col-span-2 rounded-3xl bg-red-50/60 p-8 sm:p-10 border border-red-100 flex flex-col justify-between hover:shadow-lg transition-all duration-300">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center shadow-md shadow-red-600/20">
                  <HeartHandshake className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 tracking-tight">
                  Bebas Repot Urusan HR & Ketenagakerjaan
                </h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Klien tidak perlu dipusingkan dengan urusan payroll, BPJS Kesehatan & Ketenagakerjaan, seragam, THR, izin sakit, ataupun sengketa ketenagakerjaan. Kami mengelola segalanya secara penuh dan profesional.
                </p>
              </div>

              <div className="pt-6 mt-4 border-t border-red-100">
                <div className="text-xs font-bold text-red-600 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Garansi Penggantian Personil Cepat
                </div>
              </div>
            </div>

            {/* Bento 3: Sistem Digital App */}
            <div className="reveal-on-scroll reveal-up delay-300 bento-shimmer md:col-span-1 lg:col-span-2 rounded-3xl bg-slate-50 p-8 border border-slate-200/80 hover:shadow-lg transition-all duration-300 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                  Teknologi Terintegrasi (Clean & Security App)
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Transparansi penuh melalui sistem absensi selfie barcode, pelacakan rute patroli via GPS, dan catatan log kerja harian yang dapat dipantau langsung oleh manajemen klien.
                </p>
              </div>
              <div className="pt-4 text-xs font-semibold text-slate-500">
                Real-time reporting tanpa manipulasi manual
              </div>
            </div>

            {/* Bento 4: Manajemen Handal & Supervisi Lapangan */}
            <div className="reveal-on-scroll reveal-up delay-400 bento-shimmer md:col-span-1 lg:col-span-1 rounded-3xl bg-slate-50 p-8 border border-slate-200/80 hover:shadow-lg transition-all duration-300 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-red-100 text-red-700 flex items-center justify-center">
                  <HardHat className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                  Supervisi Rutin
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Inspeksi mendadak (Sidak) dan audit operasional berkala oleh tim manajerial untuk menjamin standar SOP tetap terjaga.
                </p>
              </div>
              <div className="pt-4 text-xs font-bold text-red-600">
                Continuous Quality Control
              </div>
            </div>

            {/* Bento 5: Efisiensi Biaya */}
            <div className="reveal-on-scroll reveal-up delay-500 bento-shimmer md:col-span-1 lg:col-span-1 rounded-3xl bg-slate-50 p-8 border border-slate-200/80 hover:shadow-lg transition-all duration-300 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
                  <Building2 className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                  Efisiensi Anggaran
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Skema biaya transparan, terukur, dan dapat disesuaikan dengan skala kebutuhan fasilitas tanpa biaya tersembunyi.
                </p>
              </div>
              <div className="pt-4 text-xs font-bold text-slate-800">
                Cost-Effective Strategy
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 6. TEKNOLOGI KAMI (Feature Showcase UI - Clean App & Security App) */}
      <section id="teknologi" className="py-20 bg-slate-900 text-white relative overflow-hidden" ref={revealTeknologi}>
        {/* Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-red-600/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-12" ref={revealTeknologiTitle}>
            <div className="reveal-on-scroll reveal-up inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-red-500/20 border border-red-500/30 text-xs font-bold text-red-400 uppercase tracking-wider">
              Smart Facility Technology
            </div>
            <h2 className="reveal-on-scroll reveal-up delay-150 text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Teknologi Digital: Clean App & Security App
            </h2>
            <p className="reveal-on-scroll reveal-up delay-200 text-slate-300 text-base sm:text-lg">
              Monitoring operasional alih daya berbasis aplikasi pintar untuk akurasi data, transparansi, dan efektivitas kerja tingkat tinggi.
            </p>

            {/* Interactive App Tab Selector */}
            <div className="inline-flex p-1.5 rounded-2xl bg-slate-800/90 border border-slate-700 mt-6">
              <button
                onClick={() => setActiveAppTab('security')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                  activeAppTab === 'security'
                    ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Shield className="w-4 h-4" />
                Security App
              </button>
              <button
                onClick={() => setActiveAppTab('clean')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                  activeAppTab === 'clean'
                    ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                Clean App
              </button>
            </div>
          </div>

          {/* Feature Showcase Grid */}
          <div className="grid lg:grid-cols-12 gap-10 items-center pt-6">
            
            {/* Left: Feature List */}
            <div className="reveal-on-scroll reveal-left lg:col-span-6 space-y-4">
              
              {activeAppTab === 'security' ? (
                <div className="space-y-4 animate-tab-fade">
                  <div className="p-5 rounded-2xl bg-slate-800/70 border border-slate-700/80 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0">
                      <QrCode className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Presensi Mobile (Barcode & Selfie GPS)</h4>
                      <p className="text-slate-400 text-xs sm:text-sm mt-1">
                        Pencatatan absensi real-time dengan verifikasi foto wajah dan geofencing titik lokasi pos jaga yang presisi.
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-2xl bg-slate-800/70 border border-slate-700/80 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0">
                      <CalendarCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Multi Shift & Multi Lokasi</h4>
                      <p className="text-slate-400 text-xs sm:text-sm mt-1">
                        Pengelolaan jadwal shift (pagi, siang, malam) serta fleksibilitas rotasi antar lokasi pos yang terkoordinasi rapi.
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-2xl bg-slate-800/70 border border-slate-700/80 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0">
                      <FileSpreadsheet className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Data Log Barang & Tamu Digital</h4>
                      <p className="text-slate-400 text-xs sm:text-sm mt-1">
                        Pencatatan arus keluar-masuk kendaraan, tamu, dan inventaris barang secara digital untuk rekam jejak aman dan cepat dicari.
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-2xl bg-slate-800/70 border border-slate-700/80 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0">
                      <Activity className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Atur Lembur & Patroli Checkpoint</h4>
                      <p className="text-slate-400 text-xs sm:text-sm mt-1">
                        Validasi rute patroli di setiap checkpoint RFID/QR serta pencatatan lembur personil secara otomatis dan transparan.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 animate-tab-fade">
                  <div className="p-5 rounded-2xl bg-slate-800/70 border border-slate-700/80 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0">
                      <Fingerprint className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Presensi & Penugasan Area Higienis</h4>
                      <p className="text-slate-400 text-xs sm:text-sm mt-1">
                        Absensi tenaga cleaning dengan verifikasi lokasi zona kerja dan tracking kesiapan regu harian.
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-2xl bg-slate-800/70 border border-slate-700/80 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0">
                      <Layers className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Checklist Checklist Kebersihan Digital</h4>
                      <p className="text-slate-400 text-xs sm:text-sm mt-1">
                        Daftar periksa sanitasi toilet, lobi, koridor, dan ruang kerja dapat diisi langsung dengan bukti foto sebelum & sesudah.
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-2xl bg-slate-800/70 border border-slate-700/80 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0">
                      <CalendarCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Jadwal Perawatan Periodik (Deep Cleaning)</h4>
                      <p className="text-slate-400 text-xs sm:text-sm mt-1">
                        Notifikasi otomatis untuk agenda pembersihan berkala seperti kristalisasi marmer, poles lantai, hingga pencucian kaca gedung.
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-2xl bg-slate-800/70 border border-slate-700/80 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-400 flex items-center justify-center flex-shrink-0">
                      <Activity className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Laporan Kualitas & Rating Klien</h4>
                      <p className="text-slate-400 text-xs sm:text-sm mt-1">
                        Klien dapat memberikan rating kebersihan dan menyampaikan komplain/request instan langsung ke supervisor kami.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Right: Sleek Smartphone UI Mockup Showcase */}
            <div className="reveal-on-scroll reveal-right delay-200 lg:col-span-6 flex justify-center">
              <div className="relative w-full max-w-[340px] sm:max-w-[380px] rounded-[42px] bg-slate-950 p-4 border-[6px] border-slate-800 shadow-2xl shadow-black/80">
                
                {/* Speaker Notch */}
                <div className="w-32 h-4 bg-slate-800 rounded-full mx-auto mb-4" />

                {/* App Screen Content */}
                <div className="rounded-[28px] bg-slate-900 p-5 space-y-4 border border-slate-800/80">
                  
                  {/* Mock App Header */}
                  <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-red-600 flex items-center justify-center font-bold text-xs text-white">
                        LUJ
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-400">Integrated App</div>
                        <div className="text-xs font-bold text-white capitalize">
                          {activeAppTab === 'security' ? 'Security Portal' : 'Clean Facility Portal'}
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-semibold">
                      Online
                    </span>
                  </div>

                  {/* Mock User / Status Box */}
                  <div className="p-3.5 rounded-2xl bg-slate-800/90 border border-slate-700/60 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Petugas Bertugas:</span>
                      <span className="font-semibold text-white">Ahmad Fauzi (Regu A)</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Lokasi Penempatan:</span>
                      <span className="font-semibold text-red-400">Gedung Utama - Lt. 1</span>
                    </div>
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-700/60">
                      <span className="text-slate-400">Status Presensi:</span>
                      <span className="text-emerald-400 font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Terverifikasi GPS
                      </span>
                    </div>
                  </div>

                  {/* Mock Live Activity Feed */}
                  <div className="space-y-2">
                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                      {activeAppTab === 'security' ? 'Log Patroli Terkini' : 'Checklist Kebersihan Terkini'}
                    </div>

                    {activeAppTab === 'security' ? (
                      <div className="space-y-2">
                        <div className="p-2.5 rounded-xl bg-slate-800/50 border border-slate-800 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span>Pos Gerbang Timur</span>
                          </div>
                          <span className="text-slate-400 font-mono text-[10px]">10:45 WIB</span>
                        </div>
                        <div className="p-2.5 rounded-xl bg-slate-800/50 border border-slate-800 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span>Area Parkir VIP & Loading</span>
                          </div>
                          <span className="text-slate-400 font-mono text-[10px]">10:30 WIB</span>
                        </div>
                        <div className="p-2.5 rounded-xl bg-slate-800/50 border border-slate-800 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-400" />
                            <span>Lobby Utama & Resepsionis</span>
                          </div>
                          <span className="text-amber-300 font-mono text-[10px]">Patrol Next</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="p-2.5 rounded-xl bg-slate-800/50 border border-slate-800 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span>Sanitasi Toilet Lt. 1 & 2</span>
                          </div>
                          <span className="text-emerald-400 font-bold text-[10px]">SELESAI</span>
                        </div>
                        <div className="p-2.5 rounded-xl bg-slate-800/50 border border-slate-800 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span>Pel & Vakum Koridor Utama</span>
                          </div>
                          <span className="text-emerald-400 font-bold text-[10px]">SELESAI</span>
                        </div>
                        <div className="p-2.5 rounded-xl bg-slate-800/50 border border-slate-800 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-sky-400" />
                            <span>Pembersihan Kaca Lobby</span>
                          </div>
                          <span className="text-sky-300 font-bold text-[10px]">IN PROGRESS</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* App Action Trigger */}
                  <div className="pt-2">
                    <div className="w-full py-2.5 rounded-xl bg-gradient-to-r from-red-600 to-rose-600 text-center text-xs font-bold text-white shadow-md">
                      {activeAppTab === 'security' ? 'Lapor Log Kejadian / Tamu' : 'Kirim Laporan Harian'}
                    </div>
                  </div>

                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 7. KLIEN TERPERCAYA (Minimalist Corporate Grid) */}
      <section id="klien" className="py-20 bg-white border-b border-slate-100" ref={revealKlien}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-12" ref={revealKlienTitle}>
            <div className="reveal-on-scroll reveal-up inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-slate-100 text-xs font-bold text-slate-700 uppercase tracking-wider">
              Kemitraan Terpercaya
            </div>
            <h2 className="reveal-on-scroll reveal-up delay-150 text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight">
              Dipercaya Berbagai Institusi & Perusahaan
            </h2>
            <p className="reveal-on-scroll reveal-up delay-200 text-slate-600 text-base">
              Menjaga reputasi dan kebersihan properti klien dari berbagai sektor industri terkemuka.
            </p>
          </div>

          {/* Client Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {clients.map((client, index) => (
              <div 
                key={index} 
                className="reveal-on-scroll reveal-up client-card-hover p-6 rounded-2xl bg-slate-50 border border-slate-200/80 hover:border-red-300 hover:bg-white hover:shadow-lg transition-all duration-200 flex flex-col items-center justify-center text-center group"
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-800 font-black text-lg group-hover:text-red-600 group-hover:border-red-200 transition-colors shadow-sm mb-3">
                  {client.name.substring(0, 2).toUpperCase()}
                </div>
                <h3 className="font-bold text-slate-900 text-sm sm:text-base group-hover:text-red-600 transition-colors">
                  {client.name}
                </h3>
                <span className="text-[11px] text-slate-500 font-medium mt-1">
                  {client.tag}
                </span>
              </div>
            ))}
          </div>

          {/* Testimonial Quote Banner */}
          <div className="reveal-on-scroll reveal-up delay-200 mt-14 rounded-3xl bg-slate-900 text-white p-8 sm:p-10 border border-slate-800 relative overflow-hidden">
            <div className="relative z-10 max-w-4xl mx-auto text-center space-y-4">
              <div className="text-red-500 font-serif text-5xl leading-none">“</div>
              <p className="text-base sm:text-xl font-medium text-slate-200 italic leading-relaxed">
                PT Lima Utama Jaya memberikan standar pelayanan outsourcing yang konsisten, disiplin, dan sangat transparan dengan kehadiran sistem aplikasinya. Koordinasi tim cepat tanggap dan urusan tenaga kerja menjadi sangat mudah.
              </p>
              <div className="pt-2">
                <div className="font-bold text-white text-base">Manajemen Operasional & Fasilitas</div>
                <div className="text-xs text-red-400 font-semibold">Mitra Komersial Jawa Tengah & Nasional</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* FAQ SECTION (Accordion) */}
      <section className="py-20 bg-slate-50 border-b border-slate-100" ref={revealFaq}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center space-y-3 mb-12" ref={revealFaqTitle}>
            <div className="reveal-on-scroll reveal-up inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-red-50 text-xs font-bold text-red-600 uppercase tracking-wider">
              FAQ
            </div>
            <h2 className="reveal-on-scroll reveal-up delay-150 text-3xl font-extrabold text-slate-950 tracking-tight">
              Pertanyaan yang Sering Diajukan
            </h2>
            <p className="reveal-on-scroll reveal-up delay-200 text-slate-600 text-sm sm:text-base">
              Informasi penting seputar skema kemitraan outsourcing di PT Lima Utama Jaya.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className="reveal-on-scroll reveal-up rounded-2xl bg-white border border-slate-200/90 shadow-sm overflow-hidden transition-all duration-200"
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full p-5 sm:p-6 text-left font-bold text-slate-900 flex items-center justify-between gap-4 hover:text-red-600 transition-colors"
                >
                  <span className="text-base sm:text-lg">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180 text-red-600' : 'text-slate-400'}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-5 sm:px-6 pb-6 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100 pt-4 faq-answer-enter">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 8. KONSULTASI & HUBUNGI KAMI SECTION */}
      <section id="kontak" className="py-20 bg-white relative" ref={revealKontak}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Contact Info & Benefits */}
            <div className="reveal-on-scroll reveal-left lg:col-span-5 space-y-8">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-red-50 text-xs font-bold text-red-600 uppercase tracking-wider">
                  Hubungi Kami
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight">
                  Diskusikan Kebutuhan Alih Daya Anda
                </h2>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Konsultasikan kebutuhan personil keamanan dan fasilitas kebersihan Anda bersama konsultan ahli kami. Kami siap memberikan penawaran terbaik dan survei lokasi gratis.
                </p>
              </div>

              {/* Direct Info Cards */}
              <div className="space-y-4">
                
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase">Kantor Pusat</div>
                    <div className="font-semibold text-slate-900 text-sm mt-0.5">
                      Jl. Waru Timur Raya No.6 Pedalangan, Banyumanik, Kota Semarang
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                    <PhoneCall className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase">Telepon / WhatsApp</div>
                    <div className="font-semibold text-slate-900 text-sm mt-0.5">
                      0851-5690-6144
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase">Email Resmi</div>
                    <div className="font-semibold text-slate-900 text-sm mt-0.5">
                      ptlimautamajaya@gmail.com
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase">Website Resmi</div>
                    <div className="font-semibold text-slate-900 text-sm mt-0.5">
                      www.lujgroup.com
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Right: Interactive Consultation Form */}
            <div className="reveal-on-scroll reveal-right delay-200 lg:col-span-7">
              <div className="rounded-3xl bg-slate-50 p-8 sm:p-10 border border-slate-200 shadow-xl relative">
                
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                  Formulir Konsultasi & Permintaan Penawaran
                </h3>
                <p className="text-slate-500 text-xs sm:text-sm mb-6">
                  Isi data di bawah ini, tim representatif kami akan segera menghubungi Anda melalui WhatsApp / Telepon.
                </p>

                {formSubmitted && (
                  <div className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>Terima kasih! Pesan Anda telah diarahkan ke WhatsApp resmi PT Lima Utama Jaya.</span>
                  </div>
                )}

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Nama Lengkap *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Contoh: Bpk. Hendra Pratama"
                        className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Perusahaan / Instansi
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleFormChange}
                        placeholder="Nama PT / RS / Gedung"
                        className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Nomor WhatsApp *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleFormChange}
                        placeholder="0812-xxxx-xxxx"
                        className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Layanan yang Dibutuhkan
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      >
                        <option value="security">Jasa Keamanan (Security Services)</option>
                        <option value="cleaning">Jasa Kebersihan (Cleaning Service)</option>
                        <option value="integrated">Paket Terpadu (Security + Cleaning)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Estimasi Jumlah Personil yang Dibutuhkan
                    </label>
                    <select
                      name="personnelCount"
                      value={formData.personnelCount}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                    >
                      <option value="1-5">1 - 5 Personil</option>
                      <option value="6-15">6 - 15 Personil</option>
                      <option value="16-30">16 - 30 Personil</option>
                      <option value="30+">&gt; 30 Personil (Skala Besar)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Detail Lokasi & Catatan Tambahan
                    </label>
                    <textarea
                      name="message"
                      rows="3"
                      value={formData.message}
                      onChange={handleFormChange}
                      placeholder="Jelaskan kebutuhan spesifik atau lokasi operasional Anda..."
                      className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-bold text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/25 transition-all duration-200 active:scale-98"
                  >
                    <Send className="w-5 h-5" />
                    Kirim Permintaan via WhatsApp
                  </button>
                </form>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 9. FOOTER (Bersih & Informatif) */}
      <footer className="bg-slate-950 text-slate-300 pt-16 pb-12 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 pb-12 border-b border-slate-800/80">
            
            {/* Col 1: Brand & Bio (4 cols) */}
            <div className="lg:col-span-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white p-1 flex-shrink-0">
                  <img src={logoLuj} alt="PT Lima Utama Jaya" className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-white text-lg leading-tight">PT LIMA UTAMA JAYA</span>
                  <span className="text-[11px] text-red-500 font-semibold tracking-wider uppercase">Outsourcing Solution</span>
                </div>
              </div>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Penyedia jasa alih daya keamanan (Security Services) dan kebersihan (Cleaning Services) profesional, terpercaya, dan berizin resmi dengan dukungan teknologi aplikasi pintar.
              </p>
              <div className="flex items-center gap-3 pt-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-[11px] text-slate-300 font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5 text-red-500" /> Izin BUJP Resmi
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-[11px] text-slate-300 font-semibold">
                  <Award className="w-3.5 h-3.5 text-red-500" /> Sertifikasi Ketenagakerjaan
                </span>
              </div>
            </div>

            {/* Col 2: Navigation Links (2 cols) */}
            <div className="lg:col-span-2 space-y-3">
              <h4 className="text-xs font-bold text-white uppercase tracking-wider">Navigasi</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
                <li><a href="#beranda" className="hover:text-red-400 transition-colors">Beranda</a></li>
                <li><a href="#profil" className="hover:text-red-400 transition-colors">Profil Perusahaan</a></li>
                <li><a href="#layanan" className="hover:text-red-400 transition-colors">Layanan Utama</a></li>
                <li><a href="#keunggulan" className="hover:text-red-400 transition-colors">Keunggulan Bento</a></li>
                <li><a href="#teknologi" className="hover:text-red-400 transition-colors">Teknologi Aplikasi</a></li>
                <li><a href="#klien" className="hover:text-red-400 transition-colors">Klien Kami</a></li>
              </ul>
            </div>

            {/* Col 3: Layanan Kami (3 cols) */}
            <div className="lg:col-span-3 space-y-3">
              <h4 className="text-xs font-bold text-white uppercase tracking-wider">Layanan Alih Daya</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
                <li>✓ Satpam / Security Gedung & Pabrik</li>
                <li>✓ Pengamanan Kawasan & Residensial</li>
                <li>✓ Jasa Cleaning Service Kantor & RS</li>
                <li>✓ Perawatan Lantai & Kaca High-Rise</li>
                <li>✓ General Deep Cleaning & Disinfeksi</li>
                <li>✓ Integrasi Clean & Security App</li>
              </ul>
            </div>

            {/* Col 4: Kontak & Kantor Pusat (3 cols) */}
            <div className="lg:col-span-3 space-y-3">
              <h4 className="text-xs font-bold text-white uppercase tracking-wider">Kantor Pusat</h4>
              <div className="space-y-2.5 text-xs sm:text-sm text-slate-400">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>Jl. Waru Timur Raya No.6 Pedalangan, Banyumanik, Kota Semarang</span>
                </div>
                <div className="flex items-center gap-2">
                  <PhoneCall className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span>0851-5690-6144</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span>ptlimautamajaya@gmail.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span>www.lujgroup.com</span>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom Bar */}
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <div>
              &copy; {new Date().getFullYear()} PT Lima Utama Jaya (LUJ Group). Seluruh Hak Cipta Dilindungi.
            </div>
            <div className="flex items-center gap-6">
              <span className="hover:text-slate-400">Kebijakan Privasi</span>
              <span className="hover:text-slate-400">Syarat & Ketentuan</span>
              <span className="text-red-500 font-semibold">Semarang, Indonesia</span>
            </div>
          </div>

        </div>
      </footer>

      <button
        onClick={() => quickWhatsapp()}
        className="animate-wa-btn fixed bottom-6 right-6 z-40 flex items-center gap-2.5 px-4 py-3 rounded-full bg-emerald-600 text-white font-bold text-sm shadow-xl hover:bg-emerald-700 hover:scale-105 active:scale-95 transition-all duration-200 border-2 border-white/40 group"
        aria-label="Chat WhatsApp PT LUJ"
      >
        <MessageCircle className="w-5 h-5 fill-current" />
        <span className="hidden sm:inline">WhatsApp Fast Response</span>
      </button>

    </div>
  );
}
